import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Stack, Chip, Alert, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Avatar,
  Accordion, AccordionSummary, AccordionDetails, Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  PlayArrow as PreviewIcon, Download as DownloadIcon, CheckCircle, Cancel,
  ExpandMore, Description as FileIcon, Print as PrintIcon, Save as SaveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import config from '../../../config';
import { toast } from 'react-toastify';

interface SourceAcct { accountCode: string; accountName: string; amount: number; }
interface Fact {
  elementName: string; concept: string; labelEn?: string; labelAr?: string;
  statement: string; periodType: string; periodKey: string; value: number; isComputed: boolean;
  sourceAccounts: SourceAcct[];
}
interface Check { name: string; nameAr: string; left: number; right: number; difference: number; passed: boolean; }
interface Coverage { totalFinalAccountsWithBalance: number; mappedCount: number; unmappedCount: number; unmappedAbsAmount: number; unmappedAccounts: any[]; }
interface GenResult {
  runId?: number; fromDate?: string; toDate?: string; entityIdentifier: string; decimals: number;
  instantPeriod: string; durationPeriod: string; facts: Fact[]; checks: Check[]; warnings: string[];
  coverage?: Coverage; packagePath?: string; reportJson?: string;
}

const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
const toYmd = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';

const XbrlGeneratorPanel: React.FC<{ isRTL: boolean }> = ({ isRTL }) => {
  const [fromDate, setFromDate] = useState<Date | null>(new Date(2026, 0, 1));
  const [toDate, setToDate] = useState<Date | null>(new Date(2026, 11, 31));
  const [cr, setCr] = useState('');
  const [loading, setLoading] = useState<'' | 'preview' | 'generate'>('');
  const [res, setRes] = useState<GenResult | null>(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const label = (f: Fact) => (isRTL ? (f.labelAr || f.elementName) : (f.labelEn || f.elementName));

  const run = async (generate: boolean) => {
    setLoading(generate ? 'generate' : 'preview');
    try {
      const qs = `fromDate=${toYmd(fromDate)}&toDate=${toYmd(toDate)}${cr ? `&entityIdentifier=${encodeURIComponent(cr)}` : ''}`;
      const url = `${config.API_BASE_URL}/xbrl/${generate ? 'generate' : 'preview'}?${qs}`;
      const r = generate ? await axios.post(url, {}, auth()) : await axios.get(url, auth());
      setRes(r.data);
      toast.success(isRTL ? (generate ? 'تم التوليد والحفظ' : 'تمت المعاينة') : 'Done');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || (isRTL ? 'فشل الطلب' : 'Request failed'));
    } finally {
      setLoading('');
    }
  };

  const downloadJson = () => {
    if (!res?.reportJson) return;
    const blob = new Blob([res.reportJson], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `report_${res.fromDate?.substring(0, 10)}_${res.toDate?.substring(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const printReport = () => {
    if (!res) return;
    const title = isRTL ? 'ملف الوسم XBRL — قِيد' : 'XBRL Filing — Qaid';
    const period = `${res.fromDate?.substring(0, 10)} → ${res.toDate?.substring(0, 10)}`;
    const cols = isRTL ? ['العنصر', 'القائمة', 'النوع', 'القيمة (د.ك)'] : ['Element', 'Statement', 'Period', 'Value (KWD)'];
    const rows = res.facts.map(f => `
      <tr class="${f.isComputed ? 'computed' : ''}">
        <td>${label(f)}${f.isComputed ? '' : `<div class="code">${f.elementName}</div>`}</td>
        <td>${f.statement}</td><td>${f.periodType}</td>
        <td class="num ${f.value < 0 ? 'neg' : ''}">${fmt(f.value)}</td>
      </tr>`).join('');
    const checks = res.checks.map(c =>
      `<div class="chk ${c.passed ? 'ok' : 'bad'}">${isRTL ? c.nameAr : c.name}: ${fmt(c.left)} = ${fmt(c.right)} ${c.passed ? '✓' : `(Δ ${fmt(c.difference)})`}</div>`).join('');
    const cov = res.coverage
      ? (isRTL ? `الربط: ${res.coverage.mappedCount} مربوط، ${res.coverage.unmappedCount} غير مربوط`
        : `Coverage: ${res.coverage.mappedCount} mapped, ${res.coverage.unmappedCount} unmapped`) : '';
    const html = `<!doctype html><html dir="${isRTL ? 'rtl' : 'ltr'}" lang="${isRTL ? 'ar' : 'en'}"><head><meta charset="utf-8"><title>${title}</title>
      <style>
        body{font-family:'Segoe UI',Tahoma,Arial,sans-serif;margin:24px;color:#222}
        h1{font-size:20px;margin:0 0 4px}
        .sub{color:#666;font-size:13px;margin-bottom:12px}
        .cr{font-size:13px;margin-bottom:10px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th,td{border:1px solid #ddd;padding:7px 9px;text-align:${isRTL ? 'right' : 'left'}}
        th{background:#5a67d8;color:#fff}
        td.num{text-align:${isRTL ? 'left' : 'right'};font-variant-numeric:tabular-nums;font-weight:600}
        td.neg{color:#c62828}
        tr.computed{background:#eef2ff;font-weight:700}
        .code{color:#999;font-size:10px;direction:ltr}
        .chk{display:inline-block;margin:2px 6px 2px 0;padding:4px 10px;border-radius:20px;font-size:12px}
        .chk.ok{background:#e6f4ea;color:#1e7e34;border:1px solid #b7dfc0}
        .chk.bad{background:#fdecea;color:#c62828;border:1px solid #f5c6c2}
        .cov{margin:8px 0;font-size:13px;color:#333}
        @media print{button{display:none}}
      </style></head><body>
      <h1>${title}</h1>
      <div class="sub">al kuwait int — ${period}</div>
      <div class="cr">${isRTL ? 'رقم السجل التجاري' : 'CR Number'}: ${res.entityIdentifier || (isRTL ? '(غير مُدخل)' : '(not set)')} &nbsp;|&nbsp; ${isRTL ? 'العملة' : 'Currency'}: KWD</div>
      <div>${checks}</div><div class="cov">${cov}</div>
      <table><thead><tr>${cols.map(c => `<th>${c}</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error(isRTL ? 'اسمح بالنوافذ المنبثقة للطباعة' : 'Allow pop-ups to print'); return; }
    w.document.write(html); w.document.close();
  };

  const dpSlot = { textField: { fullWidth: true, size: 'small' as const, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } };

  return (
    <Paper elevation={0} sx={{ mt: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header band */}
      <Box sx={{ background: GRADIENT, color: '#fff', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
          <FileIcon sx={{ color: '#fff' }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {isRTL ? 'مولّد ملف الوسم (XBRL – قِيد)' : 'XBRL Filing Generator (Qaid)'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {isRTL
              ? 'يبني ملف xBRL-JSON بمعيار IFRS 2025 — للقراءة فقط، لا يغيّر أي قيد أو تقرير.'
              : 'Builds an IFRS 2025 xBRL-JSON file — read-only, changes no entry or report.'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Inputs */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
            <DatePicker label={isRTL ? 'من تاريخ' : 'From'} value={fromDate} onChange={setFromDate}
              format="dd/MM/yyyy" views={['year', 'month', 'day']} slotProps={dpSlot} />
            <DatePicker label={isRTL ? 'إلى تاريخ' : 'To'} value={toDate} onChange={setToDate}
              format="dd/MM/yyyy" views={['year', 'month', 'day']} slotProps={dpSlot} />
            <TextField label={isRTL ? 'رقم السجل التجاري' : 'CR Number'} size="small" fullWidth
              value={cr} onChange={e => setCr(e.target.value)} placeholder="123456"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          </Stack>

          {/* Buttons */}
          <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
            <Button variant="outlined" startIcon={loading === 'preview' ? <CircularProgress size={18} /> : <PreviewIcon />}
              disabled={!!loading} onClick={() => run(false)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 2.5 }}>
              {isRTL ? 'معاينة' : 'Preview'}
            </Button>
            <Button variant="contained" startIcon={loading === 'generate' ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={!!loading} onClick={() => run(true)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 2.5, background: GRADIENT, boxShadow: '0 6px 18px rgba(102,126,234,0.35)' }}>
              {isRTL ? 'توليد وحفظ' : 'Generate & Save'}
            </Button>
            {res?.reportJson && (
              <Tooltip title="report.json">
                <Button variant="outlined" color="secondary" startIcon={<DownloadIcon />} onClick={downloadJson}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                  {isRTL ? 'تنزيل الملف' : 'Download'}
                </Button>
              </Tooltip>
            )}
            {res && (
              <Button variant="outlined" color="info" startIcon={<PrintIcon />} onClick={printReport}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                {isRTL ? 'طباعة' : 'Print'}
              </Button>
            )}
          </Stack>
        </LocalizationProvider>

        {res && (
          <Box>
            {res.warnings?.map((w, i) => (
              <Alert key={i} severity="warning" sx={{ mb: 1, borderRadius: 2 }}>{w}</Alert>
            ))}

            {/* Balance checks */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              {res.checks?.map((c, i) => (
                <Chip key={i} icon={c.passed ? <CheckCircle /> : <Cancel />}
                  color={c.passed ? 'success' : 'error'} variant={c.passed ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700, py: 2, borderRadius: 2, '& .MuiChip-label': { px: 1.5 } }}
                  label={`${isRTL ? c.nameAr : c.name}: ${fmt(c.left)} = ${fmt(c.right)}${c.passed ? '' : ` (Δ ${fmt(c.difference)})`}`} />
              ))}
            </Stack>

            {/* Coverage */}
            {res.coverage && (
              <Alert severity={res.coverage.unmappedCount === 0 ? 'success' : 'info'} sx={{ mb: 2, borderRadius: 2 }}>
                {isRTL
                  ? `الربط: ${res.coverage.mappedCount} مربوط، ${res.coverage.unmappedCount} غير مربوط (${fmt(res.coverage.unmappedAbsAmount)}).`
                  : `Coverage: ${res.coverage.mappedCount} mapped, ${res.coverage.unmappedCount} unmapped (${fmt(res.coverage.unmappedAbsAmount)}).`}
              </Alert>
            )}

            {/* Facts table */}
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
              {isRTL ? `الحقائق (${res.facts?.length || 0})` : `Facts (${res.facts?.length || 0})`}
            </Typography>
            <Box sx={{ overflowX: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { background: GRADIENT, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'العنصر' : 'Element'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'القائمة' : 'Stmt'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'النوع' : 'Period'}</TableCell>
                    <TableCell align="right" sx={{ color: '#fff !important' }}>{isRTL ? 'القيمة (د.ك)' : 'Value (KWD)'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {res.facts?.map((f, i) => (
                    <TableRow key={i} hover
                      sx={f.isComputed ? { bgcolor: 'rgba(102,126,234,0.08)' } : undefined}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={f.isComputed ? 800 : 500}>{label(f)}</Typography>
                        {!f.isComputed && (
                          <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr', display: 'block' }}>{f.elementName}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={f.statement} variant="outlined"
                          color={f.statement === 'PL' ? 'warning' : 'primary'} sx={{ height: 22, fontWeight: 700 }} />
                      </TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{f.periodType}</Typography></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}
                          color={f.value < 0 ? 'error.main' : 'text.primary'}>{fmt(f.value)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Raw JSON preview */}
            {res.reportJson && (
              <Accordion sx={{ mt: 2, borderRadius: 2, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2" fontWeight={700}>{isRTL ? 'معاينة report.json' : 'Preview report.json'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box component="pre" sx={{ overflowX: 'auto', fontSize: 12, direction: 'ltr', bgcolor: '#1e1e2e', color: '#cdd6f4', p: 1.5, borderRadius: 2, maxHeight: 360 }}>
                    {res.reportJson}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default XbrlGeneratorPanel;
